import styles from './ProfileButton.module.scss';
import React, { useEffect, useRef, useState } from 'react';
import { useOutsideClick } from '../../../utils/constants/hooks';
import clsx from 'clsx';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import ReactDOM from 'react-dom';
import {
    clearLocalStorageOnLogout,
    copyInviteToClipboard,
    getInitialsFromName,
    getSubdomain,
    getUserDataFromStorage,
    isLenderType,
    isPnoUser,
    isRestrictedUser,
} from '../../../utils/utils';
import { ACCESS_TYPES, MANAGE_DROPDOWN, MANAGE_USERS_TABS, RESTRICTED_ROLES } from '../../../store/investor/constants';
import { USER_TYPE } from '../../../utils/constants/user';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Mixpanel } from '../../../utils/mixpanel';
import {
    INVESTOR_LOG_OUT,
    INVESTOR_MANAGE_INVESTORS,
    INVESTOR_MANAGE_PARTNER,
    INVESTOR_MANAGE_TEAM,
} from '../../../utils/constants/mixpanelEvents/investorEvents';
import { INVESTOR_ROUTES } from '../../../utils/constants/routesConst';
import {
    blackManagePartners,
    blackManageTeam,
    crossBtnGrey,
    dropdownBlackRounded,
    integrationBlack,
    integrationPurple,
    purpleManagePartners,
    purpleManageTeam,
    mailIcon,
    callIcon,
    headphonesIcon,
    headphonesIconPurple,
    manageFieldsIcon,
} from '../../../assets/hostedassets';
import ManageDealDrawer from '../../ManageDealDrawer/ManageDealDrawer';
import { logoutUser } from '../../../store/user/userV2/actions';
import LoadingSpinner from '../../_custom/LoadingSpinner/LoadingSpinner';
import { toast } from 'react-toastify';
import IntegrationModal from '../IntegrationModal/IntegrationModal';
import InvestmentPreferenceIcon from '../../../assets/Svg/InvestmentPreferenceIcon';
import LogoutIcon from '../../../assets/Svg/Logouticon';
import YourProfileIcon from '../../../assets/Svg/YourProfileIcon';
import { setDealDrawer, setShowManageTeamDrawer } from '../../../store/investor/action';
import { setShowManageUserDrawer } from '../../../store/company/action';
import { COMPANY_LOGOUT } from '../../../utils/constants/mixpanelEvents/companyEvents';
import { PROFILE_BUTTON, PROFILE_LABEL } from '../../ManageDealDrawer/DealDrawerConstants';
import { getCompanyConfigDataAndSetFormData } from '../../../store/company/apiHelpers';

import { TermsAndConditionConfig } from '../../../modules/Company/CompanyLogin/CompanyAdditionalDetailsForm/CompanyAdditionalDetailsForm';
import { updateQueryParams } from '../../../utils/searchParamUtils';
import ManageInvestorsIcon from '../../../assets/Svg/ManageInvestorsIcon';
import CopyIcon from '../../../assets/Svg/CopyIcon';
import { VISIBILITY_KEYS } from '../../../utils/constants/visibilityKeys';
import { useFeatureVisibility, useVisibilityConfig } from '../../../utils/useVisibilityConfig';
import MagicSignIcon from '../../../assets/Svg/MagicSignIcon';
import { SUPER_ADMIN_ROLE } from '../../../modules/Investor/ManageUser/ManageUserConstants';
import { resolveManageFieldsGate } from '../../../modules/Investor/PlatformConfig/platformConfigUtils';
import { CREDITS_COPY } from '../../../modules/Credits/constants';
import { MEERA_BROADCAST_COPY } from '../../../modules/Investor/MeeraBroadcast/constants';
import useProfileButtonLogic from './useProfileButtonLogic';

const { PARTNER, INVESTOR, TEAM, COMPANY } = MANAGE_USERS_TABS;

const PROFILE_DROPDOWN = {
    YOUR_PROFILE: 'YOUR_PROFILE',
    MANAGE_USERS: 'MANAGE_USERS',
    ALL_USERS: 'ALL_USERS',
    INVESTMENT_PREFERENCE: 'INVESTMENT_PREFERENCE',
    INTEGRATIONS: 'INTEGRATIONS',
    MANAGE_FIELDS: 'MANAGE_FIELDS',
    MEERA_BROADCAST: 'MEERA_BROADCAST',
    LOGOUT: 'LOGOUT',
    COPY_INVITE_LINK: 'COPY_INVITE_LINK',
    CONTACT_US: 'CONTACT_US',
    TERMS_OF_USE: 'TERMS_OF_USE',
    PRIVACY_POLICY: 'PRIVACY_POLICY',
    MAGIC_SIGN: 'MAGIC_SIGN',
    CREDITS: 'CREDITS',
};

// Static contact details — update here if they change
const CONTACT_EMAIL = 'help@aicatech.in';
const CONTACT_PHONE = '+91-89574 31240';
const CONTACT_PHONE_HREF = '8957431240';

type ProfileOption = {
    id: string;
    label: string;
    img?: string | JSX.Element;
    hoverImg?: string | JSX.Element;
    show: boolean;
    action: () => void;
    isLoading?: boolean;
    subOptions?: Array<{
        label: string;
        show: boolean;
        action: () => void;
    }>;
};

function ProfileButton() {
    const showDealDrawer = useAppSelector((state) => state.investor.dealDrawer.open);
    const user = useAppSelector((state) => state.user);
    const allowedUserTypeForYourProfie = [USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM, USER_TYPE.INVESTOR, USER_TYPE.PARTNER].includes(
        user.userData.accountType,
    );
    const userDataFromStorage = getUserDataFromStorage();
    const isCompanySide = userDataFromStorage?.accountType === USER_TYPE.COMPANY;
    const [showIntegrationModal, setShowIntegrationModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const uploadDataJourneyData = useAppSelector((state) => state.company.uploadJourneyData);
    const isPartner = userDataFromStorage?.accountType === USER_TYPE.PARTNER;
    const isPnoRole = isPnoUser(userDataFromStorage?.accountType);
    const inviteCode = sessionStorage.getItem('invite_code') ?? localStorage.getItem('invite_code') ?? '';
    const { manageInvestorsVisibility, managePartnersVisibility } = useFeatureVisibility({
        manageInvestorsVisibility: VISIBILITY_KEYS.LENDER_MANAGEMENT.MANAGE_INVESTORS,
        managePartnersVisibility: VISIBILITY_KEYS.PARTNER_MANAGEMENT.MANAGE_PARTNERS,
    });
    const { magicSignGlobalVisibility, openGlobalMagicSign, creditsDashboardGateOpen, openCreditsDashboard } = useProfileButtonLogic();

    const { config: visibilityConfig, isExplicitlyEnabled } = useVisibilityConfig();
    const isSuperAdmin = user.userData?.roles?.[0] === SUPER_ADMIN_ROLE;
    const canManageFields = resolveManageFieldsGate(visibilityConfig?.[VISIBILITY_KEYS.PLATFORM_CONFIG.MANAGE_FIELDS], isSuperAdmin);
    const canBroadcast = isExplicitlyEnabled(VISIBILITY_KEYS.MEERA_BROADCASTS.SEND);
    const openDealDrawer = (rowClicked = false, investmentPreferenceId = '') => {
        if (params.has('orgId')) {
            params.delete('orgId');
            setParams(params);
        }
        dispatch(
            setDealDrawer({
                open: true,
                drawerLabel: PROFILE_LABEL,
                drawerSource: PROFILE_BUTTON,
                ...(isPartner && { userType: USER_TYPE.PARTNER }),
            }),
        );
    };

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const buttonRef = useRef(null);
    const userData = user.userData;
    const isUserRestricted = isRestrictedUser(user?.userData?.roles?.[0], RESTRICTED_ROLES);
    const isLender = isLenderType(userDataFromStorage?.accountType);
    const permissions = useAppSelector((state) => state.investor.permissions);
    const subDomain = getSubdomain();
    const demoOrgId = user.domainMetadata?.demoOrgId;
    const [options, setOptions] = useState<ProfileOption[]>([]);
    const [params, setParams] = useSearchParams();
    const [showOptions, setShowOptions] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const isMagicLink = searchParams.get('configName') === 'MAGIC_LINK';
    const [termsAndPolicy, setTermsAndPolicy] = useState<TermsAndConditionConfig>();

    const [hoveredOptionId, setHoveredOptionId] = useState<string | null>(null);
    useOutsideClick(buttonRef, () => {
        setShowOptions(false);
    });

    useEffect(() => {
        if (permissions.permissionsFetched && permissions.TRACK === ACCESS_TYPES.LOCKED) {
            setOptions((options) => {
                return options.filter((option) => option.id !== PROFILE_DROPDOWN.INVESTMENT_PREFERENCE);
            });
        }
    }, [permissions]);

    const setIsLoadingForOption = (optionId: string, loadingValue: boolean) => {
        setOptions((options) =>
            options.map((option) => ({
                ...option,
                ...(option.id === optionId && { isLoading: loadingValue }),
            })),
        );
    };

    const logoutUserFromApp = () => {
        setIsLoadingForOption(PROFILE_DROPDOWN.LOGOUT, true);
        dispatch(
            logoutUser(null, {
                onSuccess: () => {
                    setIsLoadingForOption(PROFILE_DROPDOWN.LOGOUT, false);
                },
            }),
        );
        if (isCompanySide) {
            Mixpanel.track(COMPANY_LOGOUT);
            navigate('/login');
        } else {
            Mixpanel.track(INVESTOR_LOG_OUT);
            navigate('/investor');
        }
        clearLocalStorageOnLogout();
    };

    const callMixPanel = (label) => {
        switch (label) {
            case MANAGE_DROPDOWN.MANAGE_TEAM:
                Mixpanel.track(INVESTOR_MANAGE_TEAM, {
                    userName: user?.userData?.userName,
                    userEmail: user?.userData?.primaryEmail,
                    PNOName: subDomain,
                });
                break;
            case MANAGE_DROPDOWN.MANAGE_INVESTORS:
                Mixpanel.track(INVESTOR_MANAGE_INVESTORS, {
                    userName: user?.userData?.userName,
                    userEmail: user?.userData?.primaryEmail,
                    PNOName: subDomain,
                });
                break;
            case MANAGE_DROPDOWN.MANAGE_PARTNERS:
                Mixpanel.track(INVESTOR_MANAGE_PARTNER, {
                    userName: user?.userData?.userName,
                    userEmail: user?.userData?.primaryEmail,
                    PNOName: subDomain,
                });
                break;
        }
    };

    const goToManageUser = (label) => {
        setShowOptions(false);
        setParams(params);
        callMixPanel(label);
        navigate(`${INVESTOR_ROUTES.MANAGE_USER}?category=${params.get('category')?.toString()}`);
    };

    const goToManageEligibility = () => {
        Mixpanel.track('Manage Eligibility Opened', {
            userEmail: userData?.primaryEmail,
            PNOId: userData?.investorOrgId,
            PNOName: subDomain,
        });
        setShowOptions(false);
        navigate(INVESTOR_ROUTES.MANAGE_ELIGIBILITY);
    };

    const trackSupportButtonClicked = () => {
        Mixpanel.track('Support_Button_Clicked', {
            orgId: userData?.assesseeOrgId ?? params.get('orgId') ?? '',
            userEmail: userData?.primaryEmail ?? userData?.investorPrimaryEmail ?? '',
            pnoId: userData?.investorOrgId ?? '',
            userType: userData?.accountType ?? '',
            UserId: userData?.userId ?? '',
        });
    };

    const handleManageTeamAction = (role, params, setParams, location, dispatch, goToManageUser) => {
        setShowOptions(false);
        updateQueryParams(params, setParams, { category: role || TEAM }, location);

        switch (role) {
            case COMPANY:
                dispatch(setShowManageUserDrawer(true));
                break;
            case PARTNER:
            case INVESTOR:
                dispatch(setShowManageTeamDrawer(true));
                break;
            default:
                goToManageUser(MANAGE_DROPDOWN.MANAGE_TEAM);
                break;
        }
    };

    const getOptionVisibility = (option: ProfileOption): boolean => {
        const { id, label } = option;

        if (isCompanySide) {
            return (
                (id === PROFILE_DROPDOWN.MANAGE_USERS && label === MANAGE_DROPDOWN.MANAGE_TEAM) ||
                id === PROFILE_DROPDOWN.LOGOUT ||
                id === PROFILE_DROPDOWN.TERMS_OF_USE
            );
        }

        switch (id) {
            case PROFILE_DROPDOWN.YOUR_PROFILE:
                return allowedUserTypeForYourProfie;
            case PROFILE_DROPDOWN.MAGIC_SIGN:
                return magicSignGlobalVisibility?.show ?? false;
            case PROFILE_DROPDOWN.MANAGE_USERS:
                switch (label) {
                    case MANAGE_DROPDOWN.MANAGE_TEAM:
                        return true;
                    case MANAGE_DROPDOWN.MANAGE_PARTNERS:
                        return !isPartner && !isLender && managePartnersVisibility.show;
                    case MANAGE_DROPDOWN.MANAGE_INVESTORS:
                        return !isPartner && !isLender && manageInvestorsVisibility.show;
                    default:
                        return false;
                }
            case PROFILE_DROPDOWN.ALL_USERS:
                return !isPartner && !isLender;
            case PROFILE_DROPDOWN.MANAGE_FIELDS:
                return canManageFields && !isPartner && !isLender;
            case PROFILE_DROPDOWN.MEERA_BROADCAST:
                return canBroadcast && !isPartner && !isLender;
            case PROFILE_DROPDOWN.INTEGRATIONS:
                return !isPartner && !isUserRestricted && !isLender;
            case PROFILE_DROPDOWN.INVESTMENT_PREFERENCE:
                // A restricted user under LenderPrivateRoutes does get permissions fetched (that
                // dispatch has no !isUserRestricted guard, unlike the investor one), so TRACK can
                // be unlocked for them - and MANAGE_ELIGIBILITY is not in renderRestrictedRoutes.
                return !isUserRestricted && permissions.TRACK !== ACCESS_TYPES.LOCKED;
            case PROFILE_DROPDOWN.LOGOUT:
                return !isUserRestricted;
            case PROFILE_DROPDOWN.TERMS_OF_USE:
                return !!termsAndPolicy?.termsAndCondition || !!termsAndPolicy?.privacyPolicy;
            case PROFILE_DROPDOWN.COPY_INVITE_LINK:
                return !isLender;
            case PROFILE_DROPDOWN.CONTACT_US:
                return !isLender && !isPartner;
            case PROFILE_DROPDOWN.CREDITS:
                // PNO users only (mirrors the server's isCallerPno) AND the shared fail-closed gate:
                // a partner/lender user of the same PNO resolves the same config document.
                return isPnoRole && creditsDashboardGateOpen;
            default:
                return false;
        }
    };

    const getSubOptionVisibility = (subOption: { label: string }): boolean => {
        switch (subOption.label) {
            case 'Terms of Use':
                return !!termsAndPolicy?.termsAndCondition;
            case 'Privacy Policy':
                return !!termsAndPolicy?.privacyPolicy;
            default:
                return false;
        }
    };

    useEffect(() => {
        if (user.userData.fetched) {
            const updatedOptions: ProfileOption[] = [
                {
                    id: PROFILE_DROPDOWN.YOUR_PROFILE,
                    label: PROFILE_LABEL,
                    img: <YourProfileIcon colour="#1C1B1F" height="20" width="20" />,
                    hoverImg: <YourProfileIcon colour={'var(--primary-text-colour)'} height="20" width="20" />,
                    show: true,
                    action: () => {
                        openDealDrawer();
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.MAGIC_SIGN,
                    label: 'Magic Sign',
                    img: <MagicSignIcon colour={'var(--primary-black-text-colour)'} height="20" width="20" />,
                    hoverImg: <MagicSignIcon colour={'var(--primary-text-colour)'} height="20" width="20" />,
                    show: true,
                    action: () => {
                        openGlobalMagicSign();
                        setShowOptions(false);
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.MANAGE_USERS,
                    label: MANAGE_DROPDOWN.MANAGE_TEAM,
                    img: blackManageTeam,
                    hoverImg: purpleManageTeam,
                    show: true,
                    action: () => {
                        const role = isCompanySide ? COMPANY : isPartner ? PARTNER : isLender ? INVESTOR : TEAM;

                        handleManageTeamAction(role, params, setParams, location, dispatch, goToManageUser);
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.MANAGE_USERS,
                    label: MANAGE_DROPDOWN.MANAGE_PARTNERS,
                    img: blackManagePartners,
                    hoverImg: purpleManagePartners,
                    show: true,
                    action: () => {
                        params.set('category', PARTNER);
                        goToManageUser(MANAGE_DROPDOWN.MANAGE_PARTNERS);
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.MANAGE_USERS,
                    label: MANAGE_DROPDOWN.MANAGE_INVESTORS,
                    img: <ManageInvestorsIcon colour={'var(--primary-black-text-colour)'} height="20" width="20" />,
                    hoverImg: <ManageInvestorsIcon colour={'var(--primary-text-colour)'} height="20" width="20" />,
                    show: true,
                    action: () => {
                        params.set('category', INVESTOR);
                        goToManageUser(MANAGE_DROPDOWN.MANAGE_INVESTORS);
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.ALL_USERS,
                    label: 'All Users',
                    img: <YourProfileIcon colour={'var(--primary-black-text-colour)'} height="20" width="20" />,
                    hoverImg: <YourProfileIcon colour={'var(--primary-text-colour)'} height="20" width="20" />,
                    show: true,
                    action: () => {
                        setShowOptions(false);
                        navigate(INVESTOR_ROUTES.ALL_USERS);
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.MANAGE_FIELDS,
                    label: 'Manage Fields',
                    img: <img src={manageFieldsIcon} height="20" width="20" alt="" />,
                    hoverImg: <img src={manageFieldsIcon} height="20" width="20" alt="" />,
                    show: true,
                    action: () => {
                        setShowOptions(false);
                        navigate(INVESTOR_ROUTES.PLATFORM_CONFIG);
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.MEERA_BROADCAST,
                    label: MEERA_BROADCAST_COPY.PAGE_TITLE,
                    img: <CampaignOutlinedIcon sx={{ fontSize: 16, color: 'var(--primary-black-text-colour)' }} />,
                    hoverImg: <CampaignOutlinedIcon sx={{ fontSize: 16, color: 'var(--primary-text-colour)' }} />,
                    show: true,
                    action: () => {
                        setShowOptions(false);
                        navigate(`${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.MEERA_BROADCAST}`);
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.INVESTMENT_PREFERENCE,
                    label: 'Investment Preferences',
                    img: <InvestmentPreferenceIcon colour={'var(--primary-black-text-colour)'} height="16" width="16" />,
                    hoverImg: <InvestmentPreferenceIcon colour={'var(--primary-text-colour)'} height="16" width="16" />,
                    show: true,
                    action: () => {
                        goToManageEligibility();
                    },
                },
                {
                    id: PROFILE_DROPDOWN.CREDITS,
                    label: CREDITS_COPY.DASHBOARD_TITLE,
                    img: <SpeedOutlinedIcon sx={{ fontSize: 16, color: 'var(--primary-black-text-colour)' }} />,
                    hoverImg: <SpeedOutlinedIcon sx={{ fontSize: 16, color: 'var(--primary-text-colour)' }} />,
                    show: true,
                    action: openCreditsDashboard,
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.INTEGRATIONS,
                    label: 'Integrations',
                    img: integrationBlack,
                    hoverImg: integrationPurple,
                    show: true,
                    action: () => {
                        setShowIntegrationModal(true);
                    },
                    isLoading: false,
                },
                {
                    img: <CopyIcon colour={'var(--primary-black-text-colour)'} height="16" width="16" />,
                    hoverImg: <CopyIcon colour={'var(--primary-text-colour)'} height="16" width="16" />,
                    id: PROFILE_DROPDOWN.COPY_INVITE_LINK,
                    label: 'Copy Invite Link',
                    show: true,
                    action: () => copyInviteToClipboard(inviteCode),
                },
                {
                    id: PROFILE_DROPDOWN.CONTACT_US,
                    label: 'Contact Us',
                    img: headphonesIcon,
                    hoverImg: headphonesIconPurple,
                    show: true,
                    action: () => {
                        trackSupportButtonClicked();
                        setShowContactModal(true);
                    },
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.LOGOUT,
                    label: `Logout (${isCompanySide ? userDataFromStorage?.userName ?? '' : user?.userData?.userName ?? ''})`,
                    img: <LogoutIcon color={'var(--primary-black-text-colour)'} height="15" width="15" />,
                    hoverImg: <LogoutIcon color={'var(--primary-text-colour)'} height="15" width="15" />,
                    show: true,
                    action: () => logoutUserFromApp(),
                    isLoading: false,
                },
                {
                    id: PROFILE_DROPDOWN.TERMS_OF_USE,
                    label: 'TermsAndPolicy',
                    show: true,
                    action: () => {},
                    subOptions: [
                        {
                            label: 'Terms of Use',
                            show: true,
                            action: () => {
                                if (termsAndPolicy?.termsAndCondition) {
                                    window.open(termsAndPolicy.termsAndCondition, '_blank');
                                }
                            },
                        },
                        {
                            label: 'Privacy Policy',
                            show: true,
                            action: () => {
                                if (termsAndPolicy?.privacyPolicy) {
                                    window.open(termsAndPolicy.privacyPolicy, '_blank');
                                }
                            },
                        },
                    ],
                },
            ];
            const visibleOptions = updatedOptions.map((option) => {
                option.show = getOptionVisibility(option);

                if (option.subOptions) {
                    option.subOptions = option.subOptions.map((subOption) => ({
                        ...subOption,
                        show: getSubOptionVisibility(subOption),
                    }));

                    if (!option.subOptions.some((subOption) => subOption.show)) {
                        option.show = false;
                    }
                }

                return option;
            });

            setOptions(visibleOptions);
        }
    }, [
        user.userData.fetched,
        isPartner,
        isUserRestricted,
        isLender,
        isCompanySide,
        userDataFromStorage?.userName,
        user?.userData?.userName,
        termsAndPolicy,
        permissions.TRACK,
        allowedUserTypeForYourProfie,
        demoOrgId,
        magicSignGlobalVisibility?.show,
        canManageFields,
        canBroadcast,
        // getOptionVisibility reads these; leaving them out stops the entry updating once the async config resolves.
        isPnoRole,
        creditsDashboardGateOpen,
    ]);

    const renderOptionIcon = (option: ProfileOption, isHovered: boolean) => {
        if (option.isLoading) {
            return <LoadingSpinner height="14px" color="black" style={{ margin: '0' }} />;
        }

        const iconToRender = isHovered ? option.hoverImg : option.img;
        if (!iconToRender) return null;

        if (typeof iconToRender === 'string') {
            return <img src={iconToRender} alt={isHovered ? 'hover' : 'default'} height={15} width={15} className={styles.OptionImage} />;
        }

        return React.cloneElement(iconToRender);
    };
    const handleClick = () => {
        getCompanyConfigDataAndSetFormData(
            {
                accountType: userData?.accountType,
                primaryEmail: userData?.primaryEmail ?? userData.investorPrimaryEmail,
                investorOrgId: userData?.investorOrgId,
            },
            {
                onSuccess: (config, orgDisplayName) => {
                    setTermsAndPolicy(config?.termsAndPolicyConfig || {});
                    if (orgDisplayName) {
                        updateQueryParams(searchParams, setSearchParams, { orgDisplayName: orgDisplayName }, location);
                    }
                },
            },
        );

        setShowOptions((prev) => !prev);
    };

    return (
        <div className={styles.ProfileButton}>
            <div
                className={clsx(styles.MainButton, { [styles.MainButtonActive]: showOptions })}
                onClick={() => handleClick()}
                ref={buttonRef}
            >
                {!isCompanySide ? (
                    <img
                        className={styles.logoImage}
                        src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/profileDropdownImg.svg'}
                        alt="-"
                        height={20}
                    />
                ) : (
                    <div className={styles.companyLogo}>
                        <div className={styles.companyLogoPlaceholder}>
                            {getInitialsFromName(uploadDataJourneyData?.organizationName, false)}
                        </div>
                        {/* {to handle the Organization name Company Side} */}
                        {/* <div className={styles.companyLogoText}>{uploadDataJourneyData?.organizationName}</div> */}
                    </div>
                )}

                {!isMagicLink && (
                    <img
                        className={clsx(styles.DropdownImg, {
                            [styles.DropdownImgActive]: showOptions,
                        })}
                        src={dropdownBlackRounded}
                        alt="-"
                        height="6px"
                    />
                )}
                {showOptions && !isMagicLink && (
                    <div className={styles.Options}>
                        {options
                            ?.filter?.((option) => !!option?.show)
                            ?.map((option, i) => {
                                const truncatedLabel =
                                    option.id === PROFILE_DROPDOWN.LOGOUT
                                        ? option.label.length > 17
                                            ? `${option.label.slice(0, 17)}...`
                                            : option.label
                                        : option.label;
                                const hasVisibleSubOptions = option.subOptions && option.subOptions.some((subOption) => subOption.show);

                                return (
                                    <div
                                        className={clsx(styles.Option, {
                                            [styles.HasSubOptions]: hasVisibleSubOptions,
                                        })}
                                        key={i}
                                        onMouseEnter={() => setHoveredOptionId(option.label)}
                                        onMouseLeave={() => setHoveredOptionId(null)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowOptions(false);
                                            option?.action?.();
                                        }}
                                    >
                                        {hasVisibleSubOptions ? (
                                            <div className={styles.SubOptionsContainer}>
                                                <div className={styles.SubOptions}>
                                                    {option?.subOptions
                                                        ?.filter((subOption) => subOption.show)
                                                        .map((subOption, j) => (
                                                            <div
                                                                key={j}
                                                                className={styles.SubOption}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowOptions(false);
                                                                    subOption.action();
                                                                }}
                                                            >
                                                                {subOption.label}
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {renderOptionIcon(option, hoveredOptionId === option.label)}
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowOptions(false);
                                                        option?.action?.();
                                                    }}
                                                >
                                                    {truncatedLabel}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                )}
            </div>
            {/* Integration Modal */}
            {showIntegrationModal && (
                <IntegrationModal
                    open={showIntegrationModal}
                    onClose={() => setShowIntegrationModal(false)}
                    onIntegrationSuccess={() => {
                        setShowIntegrationModal(false);
                        toast.success('Integration Successful');
                    }}
                />
            )}

            {/* Contact Us Modal */}
            {showContactModal &&
                ReactDOM.createPortal(
                    <div className={styles.ContactModalOverlay} onClick={() => setShowContactModal(false)}>
                        <div className={styles.ContactModal} onClick={(e) => e.stopPropagation()}>
                            {/* Header */}
                            <div className={styles.ContactModalHeader}>
                                <span className={styles.ContactModalTitle}>Contact Us</span>
                                <button className={styles.ContactModalClose} onClick={() => setShowContactModal(false)}>
                                    <img src={crossBtnGrey} alt="close" width="24" height="24" />
                                </button>
                            </div>

                            {/* Email Row */}
                            <div className={styles.ContactModalRow}>
                                <img src={mailIcon} alt="email" width="20" height="20" />
                                <span className={styles.ContactModalText}>
                                    Email us at&nbsp;
                                    <a href={`mailto:${CONTACT_EMAIL}`} className={styles.ContactModalLink}>
                                        {CONTACT_EMAIL}
                                    </a>
                                </span>
                            </div>

                            {/* Phone Row */}
                            <div className={styles.ContactModalRow}>
                                <img src={callIcon} alt="phone" width="20" height="20" />
                                <span className={styles.ContactModalText}>
                                    Call us on:&nbsp;
                                    <a href={`tel:${CONTACT_PHONE_HREF}`} className={styles.ContactModalLink}>
                                        {CONTACT_PHONE}
                                    </a>
                                </span>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}

            {showDealDrawer && dealDrawerState?.drawerSource !== 'Debt Products' && (
                <ManageDealDrawer source={dealDrawerState?.drawerSource ?? ''} />
            )}
        </div>
    );
}

export default ProfileButton;
